import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useSpc } from '../../context/SpcContext';
import { Reveal, Stagger, Item, Pop, motion, EmptyState } from '../ui';
import { AnimatePresence } from 'framer-motion';
import { ChevronDown, Check, Clock, BarChart3, Users, User, List, CalendarDays, Repeat, CalendarPlus, Calendar, Download, Info } from 'lucide-react';
import BookCalendar from './BookCalendar';
import { formatDisplay, parseISO } from '../../lib/dates';
import { BookedMembersList } from '../Modals';
import { ScInfoBadge } from '../ScInfoBadge';
import { googleCalendarUrl, downloadIcs } from '../../lib/calendar';
import { isSCSession } from '../../lib/sessionInfo';

const displayDate = (s) => (s.dateISO ? formatDisplay(s.dateISO) : s.date);
const displayDateFull = (s) => {
  if (!s.dateISO) return s.date;
  const yr = s.dateISO.split('-')[0];
  return `${formatDisplay(s.dateISO)} ${yr}`;
};

function CostBadge({ price }) {
  const free = !price || /free/i.test(price);
  return <span className={`pill ${free ? 'green' : 'yellow'}`}>{free ? 'Free' : price}</span>;
}

function DietaryNoteEditor({ s }) {
  const { updateDietaryNote } = useSpc();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(s.myDietaryNote || '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await updateDietaryNote(s.id, val.trim()); setEditing(false); } finally { setBusy(false); }
  };
  if (editing) {
    return (
      <div style={{ flexBasis: '100%', width: '100%', marginTop: 4 }} data-testid={`dietary-note-editor-${s.id}`}>
        <textarea rows={2} maxLength={200} value={val} onChange={(e) => setVal(e.target.value)} placeholder="e.g. vegetarian, nut allergy, gluten-free…" data-testid={`dietary-note-input-${s.id}`} />
        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          <button type="button" className="btn" disabled={busy} onClick={save} data-testid={`dietary-note-save-${s.id}`}>{busy ? 'Saving…' : 'Save note'}</button>
          <button type="button" className="btn ghost" onClick={() => { setVal(s.myDietaryNote || ''); setEditing(false); }} data-testid={`dietary-note-cancel-${s.id}`}>Cancel</button>
        </div>
      </div>
    );
  }
  return (
    <div style={{ flexBasis: '100%', width: '100%', marginTop: 4 }} data-testid={`dietary-note-display-${s.id}`}>
      <span className="meta">🍽 {s.myDietaryNote ? `Your note: "${s.myDietaryNote}"` : 'No dietary note added'}</span>
      <button type="button" className="btn ghost" style={{ marginLeft: 8, padding: '2px 10px' }} onClick={() => setEditing(true)} data-testid={`dietary-note-edit-${s.id}`}>{s.myDietaryNote ? 'Edit note' : 'Add note'}</button>
    </div>
  );
}

function BookBtn({ s }) {
  const { role, user, state, bookSession, startBlockCheckout, bookBlock, rejoinBlockWeek, cancelBooking, openModal } = useSpc();
  const [pending, setPending] = useState(false);
  const [blockPending, setBlockPending] = useState(false);
  const [rejoinPending, setRejoinPending] = useState(false);
  const [paceGroup, setPaceGroup] = useState('');
  const paceDefs = state.paceGroupDefs || [];
  const paceDef = paceDefs.find(g => g.key === s.myPaceGroup);
  const canManage = role === 'admin' || (user?.permissions || []).includes('create_sessions');
  const requireSafetyCard = (fn) => {
    if (!user?.safetyCardComplete) { openModal('safetyCard', { onComplete: fn }); return; }
    fn();
  };
  const paid = !!(s.priceAmount && s.priceAmount > 0);
  const free = !paid && (!s.price || /free/i.test(s.price));
  const isPaidSocial = s.type === 'Social' && paid;
  const isClassBlock = s.type === 'Classes' && s.recurring && !!s.seriesId;
  const isMandatoryBlock = isClassBlock && !!s.blockBooking;
  const isTicketed = paid && !isMandatoryBlock;

  const manageBtn = canManage
    ? <button className="btn secondary" data-testid={`manage-session-${s.id}`} onClick={() => openModal('editSession', { id: s.id })}>Manage</button>
    : null;
  const duplicateBtn = canManage
    ? <button className="btn secondary" data-testid={`duplicate-session-${s.id}`} onClick={() => openModal('newSession', { duplicateFrom: s })} title="Copy this session's details to set up the next one">Duplicate</button>
    : null;

  let bookingEl;
  if (s.mine) {
    bookingEl = (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="pill green" data-testid={`booked-badge-${s.id}`}>Booked ✓{isTicketed && s.myTicketCount > 1 ? ` ×${s.myTicketCount}` : ''}</span>
        {s.paceGroups && paceDef && (
          <span className="pill pace-pill" data-testid={`my-pace-group-${s.id}`}>{paceDef.emoji} {paceDef.label} /mile{paceDef.kmLabel ? ` · ${paceDef.kmLabel} /km` : ''}</span>
        )}
        {isTicketed && s.booked < s.capacity && (
          <button
            className="btn secondary"
            data-testid={`buy-more-tickets-${s.id}`}
            onClick={() => requireSafetyCard(() => openModal('buyTickets', { sessionId: s.id }))}
          >
            Buy more tickets
          </button>
        )}
        {isPaidSocial ? (
          <span className="meta" data-testid={`no-cancel-note-${s.id}`}>Paid ticket — contact an admin to cancel</span>
        ) : (
          <button
            className="btn ghost"
            data-testid={`cancel-session-${s.id}`}
            onClick={() => {
              const msg = isClassBlock
                ? `Can't make "${s.title}" on ${displayDateFull(s)}? You'll stay booked on the other weeks in this block.`
                : `Cancel your booking for "${s.title}" on ${displayDateFull(s)}? Other sessions in this series stay booked.`;
              if (window.confirm(msg)) cancelBooking(s.id);
            }}
          >
            {isClassBlock ? "Can't make it this week" : 'Cancel this one'}
          </button>
        )}
        <button className="btn ghost" data-testid={`calendar-google-${s.id}`} title="Add to Google Calendar" onClick={() => window.open(googleCalendarUrl(s), '_blank')}>
          <Calendar size={14} />
        </button>
        <button className="btn ghost" data-testid={`calendar-ics-${s.id}`} title="Download calendar file (Apple/Outlook)" onClick={() => downloadIcs([s], `spc-${s.id}.ics`)}>
          <Download size={14} />
        </button>
        {s.hasCatering && <DietaryNoteEditor s={s} />}
      </div>
    );
  } else {
    const onClick = async () => {
      if (isTicketed) { openModal('buyTickets', { sessionId: s.id }); return; }
      if (s.hasCatering) { openModal('bookWithNote', { sessionId: s.id }); return; }
      if (pending) return;
      if (s.paceGroups && !paceGroup) return;
      setPending(true);
      try { await bookSession(s.id, paceGroup); } finally { setPending(false); }
    };
    const onBlockClick = async () => {
      if (blockPending) return;
      setBlockPending(true);
      try {
        if (paid) await startBlockCheckout(s.id);
        else { await bookBlock(s.id); setBlockPending(false); }
      } catch (e) { setBlockPending(false); }
    };
    if (isMandatoryBlock) {
      const priceSuffix = paid ? ` · £${Number(s.priceAmount).toFixed(2).replace(/\.00$/, '')}` : '';
      if (s.isBlockMember) {
        const onRejoin = async () => {
          if (rejoinPending) return;
          setRejoinPending(true);
          try { await rejoinBlockWeek(s.id); } finally { setRejoinPending(false); }
        };
        bookingEl = (
          <button className="btn" data-testid={`rejoin-week-btn-${s.id}`} disabled={rejoinPending} onClick={onRejoin}>
            {rejoinPending ? 'Adding you back in…' : "I can come after all"}
          </button>
        );
      } else {
        bookingEl = (
          <button className="btn" data-testid={`book-block-btn-${s.id}`} disabled={blockPending || s.booked >= s.capacity} onClick={() => requireSafetyCard(onBlockClick)}>
            {s.booked >= s.capacity ? 'Full' : blockPending ? (paid ? 'Redirecting…' : 'Booking…') : `Book now${priceSuffix}`}
          </button>
        );
      }
    } else if (s.paceGroups && !s.hasCatering) {
      bookingEl = (
        <div className="pace-book-row">
          <div className="pace-select-wrap">
            <select className="pace-group-select" value={paceGroup} onChange={(e) => setPaceGroup(e.target.value)} data-testid={`pace-group-select-${s.id}`}>
              <option value="">Pick your pace group…</option>
              {paceDefs.map(g => <option key={g.key} value={g.key}>{g.emoji} {g.label} /mile · {g.kmLabel} /km</option>)}
            </select>
            <button
              type="button"
              className="pace-info-btn"
              data-testid={`pace-info-btn-${s.id}`}
              title="What do pace groups mean?"
              aria-label="What do pace groups mean?"
              onClick={() => openModal('paceInfo')}
            >
              <Info size={14} />
            </button>
          </div>
          <button className="btn" data-testid={`book-btn-${s.id}`} disabled={pending || !paceGroup || s.booked >= s.capacity} onClick={() => requireSafetyCard(onClick)}>
            {s.booked >= s.capacity ? 'Full' : pending ? 'Booking…' : (free ? 'Book free' : `Book · ${s.price}`)}
          </button>
        </div>
      );
    } else {
      bookingEl = (
        <button className="btn" data-testid={`book-btn-${s.id}`} disabled={pending || s.booked >= s.capacity} onClick={() => requireSafetyCard(onClick)}>
          {s.booked >= s.capacity ? 'Full' : pending ? (paid ? 'Redirecting…' : 'Booking…') : (free ? 'Book free' : `Book · ${s.price}`)}
        </button>
      );
    }
  }

  if (!manageBtn) return bookingEl;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      {bookingEl}
      {manageBtn}
      {duplicateBtn}
    </div>
  );
}

function SessionCard({ s, defaultOpen, focus }) {
  const { role, state, sessionPhotoUrl } = useSpc();
  const [open, setOpen] = useState(!!defaultOpen);
  const ref = useRef(null);
  const free = !(s.priceAmount && s.priceAmount > 0) && (!s.price || /free/i.test(s.price));
  const includes = s.includes || [];
  useEffect(() => {
    if (focus) { setOpen(true); setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120); }
  }, [focus]);
  const windowWeeks = state.settings?.bookingWindowWeeks ?? 8;
  const visibility = useMemo(() => {
    if (role === 'member' || s.type === 'Social' || !s.dateISO) return null;
    const todayISO = new Date().toISOString().slice(0, 10);
    const cutoffISO = new Date(Date.now() + windowWeeks * 7 * 86400000).toISOString().slice(0, 10);
    if (s.dateISO < todayISO) return { label: 'Past — hidden from members', tone: 'soft' };
    if (s.dateISO > cutoffISO) {
      const weeksAway = Math.ceil((new Date(s.dateISO) - new Date(cutoffISO)) / (7 * 86400000));
      return { label: `Visible to members in ${weeksAway}w`, tone: 'yellow' };
    }
    return { label: 'Visible to members now', tone: 'green' };
  }, [role, s.type, s.dateISO, windowWeeks]);
  const facts = [
    { icon: <Clock size={15} />, b: s.duration || '—', s: 'Duration' },
    { icon: <BarChart3 size={15} />, b: s.level || 'All levels', s: 'Level' },
    { icon: <Users size={15} />, b: Math.max(0, s.capacity - s.booked), s: 'Spaces left' },
    { icon: <User size={15} />, b: s.coach, s: 'Coach / host' }
  ];
  return (
    <Pop className={`booking-card ${open ? 'open' : ''} ${focus ? 'focused' : ''}`} data-testid={`session-card-${s.id}`}>
      <div ref={ref} />
      <div className="bc-head">
        {s.hasPhoto && <div className="bc-thumb" data-testid={`session-photo-${s.id}`}><img src={sessionPhotoUrl(s.id, s.photoV)} alt={s.title} /></div>}
        <div className="item-main">
          <div className="bc-badges"><span className="pill yellow">{s.type}</span><CostBadge price={s.price} />{s.recurring && <span className="pill soft" data-testid={`recurring-badge-${s.id}`}><Repeat size={12} style={{ marginRight: 4, verticalAlign: '-1px' }} />Weekly</span>}{visibility && <span className={`pill ${visibility.tone}`} data-testid={`visibility-badge-${s.id}`}>{visibility.label}</span>}{isSCSession(s) && <ScInfoBadge testId={`sc-info-badge-${s.id}`} />}</div>
          <div className="item-title">{s.title}</div>
          <div className="meta"><span>{displayDate(s)} · {s.time}</span><span>{s.location}</span><span>{s.coach}</span><span>{s.booked}/{s.capacity} booked</span>{s.type === 'Classes' && s.recurring && role !== 'member' && s.blockBookedCount != null && <span data-testid={`block-planning-${s.id}`}>{s.blockBookedCount} total block members · {s.booked} confirmed this week{s.blockBookedCount > s.booked ? ` · ${s.blockBookedCount - s.booked} sitting out this week` : ''}</span>}</div>
        </div>
        <div className="bc-actions">
          <button className="bc-toggle" onClick={() => setOpen(o => !o)} aria-expanded={open}>
            {open ? 'Less' : 'Details'} <ChevronDown size={16} style={{ transform: open ? 'rotate(180deg)' : 'none' }} />
          </button>
          <BookBtn s={s} />
        </div>
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div key="body" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: 'hidden' }}>
            <div className="bc-body">
              <p>{s.description || 'More details for this session will appear here soon.'}</p>
              <div className="bc-facts">
                {facts.map((f, i) => (
                  <div className="fact" key={i}><span className="fi">{f.icon}</span><span><b>{f.b}</b><span>{f.s}</span></span></div>
                ))}
              </div>
              {includes.length > 0 && (
                <ul className="includes">
                  {includes.map((it, i) => <li key={i}><span className="tick"><Check size={13} /></span>{it}</li>)}
                </ul>
              )}
              <BookedMembersList s={s} />
              <div className="bc-cta">
                <div className="bc-price">
                  {free ? <span className="free">Free for members</span> : <><span className="paid">{s.price}</span> <span style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 600 }}>per member</span></>}
                </div>
                {!free && s.memberPaysFee && s.cardFee > 0 && (
                  <div className="meta" style={{ marginTop: 2 }} data-testid={`session-fee-note-${s.id}`}>+ £{s.cardFee.toFixed(2)} card fee at checkout (total £{(s.priceAmount + s.cardFee).toFixed(2)})</div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Pop>
  );
}

const PARKRUN_LOGO_URL = 'https://customer-assets-jt897jd0.emergentagent.net/job_spc-booking-app/artifacts/baab60a0ccd485e6_images.png';

function ParkrunCard({ s, go }) {
  const { state } = useSpc();
  const pr = state.parkrun || { myLocation: null, groups: [] };
  const isNearest = s.dateISO === pr.date;
  const totalGoing = isNearest ? pr.groups.reduce((n, g) => n + g.attendees.length, 0) : 0;
  return (
    <Pop className="booking-card" data-testid={`session-card-${s.id}`}>
      <div className="bc-head">
        <div className="bc-thumb parkrun-thumb" data-testid={`session-photo-${s.id}`}><img src={PARKRUN_LOGO_URL} alt="parkrun" /></div>
        <div className="item-main">
          <div className="bc-badges">
            <span className="pill yellow">Parkrun</span>
            <CostBadge price={s.price} />
            <span className="pill soft" data-testid={`recurring-badge-${s.id}`}><Repeat size={12} style={{ marginRight: 4, verticalAlign: '-1px' }} />Weekly</span>
          </div>
          <div className="item-title">Parkrun</div>
          <div className="meta">
            <span>{displayDate(s)} · {s.time}</span>
            <span>Pick your own location</span>
            {totalGoing > 0 && <span>{totalGoing} SPC member{totalGoing === 1 ? '' : 's'} going</span>}
          </div>
        </div>
        <div className="bc-actions">
          <button className="btn" data-testid={`parkrun-open-btn-${s.id}`} onClick={() => go('parkrun')}>
            {isNearest ? (pr.myLocation ? `You're in · ${pr.myLocation}` : "I'm in") : 'Open Parkrun'}
          </button>
        </div>
      </div>
    </Pop>
  );
}

export default function Book({ go }) {
  const { state, role, user, openModal, pendingFocus, consumeFocus } = useSpc();
  const canCreateSessions = role === 'admin' || (user?.permissions || []).includes('create_sessions');
  const [mode, setMode] = useState('list');
  const [day, setDay] = useState(null);
  const [focusId, setFocusId] = useState(null);
  const [hidePast, setHidePast] = useState(true);

  // Members only see the next `bookingWindowWeeks` — but Socials always show, and
  // admins/coaches see everything (each card shows when it'll become visible to members),
  // with an optional toggle to hide past sessions from cluttering the Manage list.
  const windowWeeks = state.settings?.bookingWindowWeeks ?? 8;
  // Parkrun recurs every Saturday — generate enough future occurrences so it shows on
  // every Saturday the admin/member browses to in the calendar, not just the next one.
  const parkrunPseudoList = useMemo(() => {
    if (!state.parkrun?.date) return [];
    const base = new Date(`${state.parkrun.date}T00:00:00Z`);
    return Array.from({ length: 20 }, (_, i) => {
      const iso = new Date(base.getTime() + i * 7 * 86400000).toISOString().slice(0, 10);
      return { id: `parkrun-${iso}`, dateISO: iso, time: '09:00', title: 'Parkrun', type: 'Parkrun', price: 'Free', isParkrun: true };
    });
  }, [state.parkrun?.date]);
  const sorted = useMemo(() => {
    const list = [...state.sessions, ...parkrunPseudoList].sort((a, b) => String(a.dateISO || '').localeCompare(String(b.dateISO || '')));
    if (role !== 'member') {
      if (!hidePast) return list;
      const todayISO = new Date().toISOString().slice(0, 10);
      return list.filter(s => !s.dateISO || s.dateISO >= todayISO);
    }
    const todayISO = new Date().toISOString().slice(0, 10);
    const cutoffISO = new Date(Date.now() + windowWeeks * 7 * 86400000).toISOString().slice(0, 10);
    // Socials are exempt from the upper-bound window cutoff (they can be booked far ahead),
    // but never from the lower bound — a past Social must never linger in a member's list.
    return list.filter(s => s.dateISO && s.dateISO >= todayISO && (s.type === 'Social' || s.isParkrun || s.dateISO <= cutoffISO));
  }, [state.sessions, parkrunPseudoList, role, windowWeeks, hidePast]);
  const shown = useMemo(() => {
    // Flat list (no day selected): show every upcoming session in date order, including one
    // Parkrun card per Saturday — same Saturdays the calendar puts a chip on. Parkrun is exempt
    // from the booking-window cutoff in `sorted` (like Socials) so the Calendar can be browsed
    // months ahead, but the flat LIST should still cap at the member's normal window, or it
    // turns into a long run of near-identical weekly cards.
    if (!day) {
      if (role !== 'member') return sorted;
      const cutoffISO = new Date(Date.now() + windowWeeks * 7 * 86400000).toISOString().slice(0, 10);
      return sorted.filter(s => !s.isParkrun || s.dateISO <= cutoffISO);
    }
    return sorted.filter(s => { const dt = parseISO(s.dateISO); return dt && dt.getFullYear() === day.y && dt.getMonth() === day.m && dt.getDate() === day.d; });
  }, [sorted, day, role, windowWeeks]);

  const selLabel = day ? formatDisplay(`${day.y}-${String(day.m + 1).padStart(2, '0')}-${String(day.d).padStart(2, '0')}`) : null;
  const setModeSafe = (m) => { setMode(m); if (m === 'list') setDay(null); };

  const pickSession = (s) => {
    const dt = parseISO(s.dateISO);
    if (dt) setDay({ y: dt.getFullYear(), m: dt.getMonth(), d: dt.getDate() });
    setFocusId(s.id);
    setTimeout(() => setFocusId(null), 1400);
  };

  // Deep-link from Home Coming Up tiles / Featured Event — jump straight to this one session
  // in the full list (never narrow to "everything on this day", which could surface an
  // unrelated session that happens to share the same date and look like the wrong thing opened).
  useEffect(() => {
    if (!pendingFocus) return;
    const s = state.sessions.find(x => x.id === pendingFocus);
    if (s) {
      setMode('list');
      setDay(null);
      setFocusId(s.id);
      setTimeout(() => setFocusId(null), 1400);
    }
    consumeFocus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFocus, state.sessions]);

  return (
    <>
      <Reveal className="section-head">
        <div><div className="eyebrow">{role === 'member' ? 'Book' : 'Manage'}</div><h2>{role === 'member' ? 'Book with SPC' : 'Sessions & events'}</h2><p>Runs, socials, workshops and events. Switch to <strong style={{ color: '#fff' }}>Calendar</strong> to browse by date, or tap <strong style={{ color: '#fff' }}>Details</strong> for what's included and the cost.</p></div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {role !== 'member' && (
            <label className="past-toggle" data-testid="hide-past-toggle" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--muted)', cursor: 'pointer' }}>
              <input type="checkbox" checked={hidePast} onChange={(e) => setHidePast(e.target.checked)} />
              Hide past sessions
            </label>
          )}
          <div className="seg">
            <button data-testid="book-mode-list" className={mode === 'list' ? 'active' : ''} onClick={() => setModeSafe('list')}><List size={15} />List</button>
            <button data-testid="book-mode-calendar" className={mode === 'calendar' ? 'active' : ''} onClick={() => setModeSafe('calendar')}><CalendarDays size={15} />Calendar</button>
          </div>
          {canCreateSessions && <button className="btn" data-testid="create-session-btn" onClick={() => openModal('newSession')}>+ Create</button>}
        </div>
      </Reveal>

      {mode === 'calendar' && (
        <Reveal><BookCalendar sessions={sorted} selected={day} onSelect={setDay} onPickSession={pickSession} /></Reveal>
      )}

      {selLabel && (
        <div className="section-head" style={{ margin: '22px 4px 12px' }}><div><div className="eyebrow">Selected day</div><h2 style={{ fontSize: 20 }}>{selLabel} · {shown.length} {shown.length === 1 ? 'session' : 'sessions'}</h2></div></div>
      )}

      <div className="list" style={{ marginTop: mode === 'calendar' && !selLabel ? 22 : 16 }} data-testid="sessions-list">
        {shown.length ? shown.map(s => (s.isParkrun ? <ParkrunCard key={s.id} s={s} go={go} /> : <SessionCard key={s.id} s={s} focus={focusId === s.id} />)) : (
          day ? (
            <EmptyState
              icon={CalendarDays}
              title="Nothing on this day"
              message="Pick another day on the calendar or switch to List to see everything coming up."
              testId="sessions-empty"
            />
          ) : state.sessions.length === 0 ? (
            <EmptyState
              icon={CalendarPlus}
              title="No sessions yet"
              message={canCreateSessions ? 'Create your first session and members can start booking straight away.' : "Check back soon — a new session is usually just around the corner."}
              action={canCreateSessions ? <button className="btn" onClick={() => openModal('newSession')} data-testid="empty-create-session">+ Create the first session</button> : null}
              testId="sessions-empty"
            />
          ) : (
            <EmptyState
              icon={CalendarDays}
              title="No upcoming sessions"
              message="Everything upcoming has been booked. Watch the notifications bell — new sessions get announced here first."
              testId="sessions-empty"
            />
          )
        )}
      </div>
    </>
  );
}
